import AssessmentIcon from '@material-ui/icons/Assessment';
import BoardIcon from '@material-ui/icons/DeveloperBoard';
import GetAppIcon from '@material-ui/icons/GetApp';
import MemoryIcon from '@material-ui/icons/Memory';
import PageviewIcon from '@material-ui/icons/Pageview';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import EditIcon from '@material-ui/icons/Edit';
import PublishIcon from '@material-ui/icons/Publish';
import WavesIcon from '@material-ui/icons/Waves';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import VocabularyManagerIcon from '../../core/icons/VocabularyManager';
import PageLayout from '../../core/PageLayout';
import ConceptResultDisplay from '../advanced-search/ConceptResultDisplay';
import PrivateRoute from '../../core/PrivateRoute';
import { BASE_OPTIONS } from '../../core/project/constants';
import ProjectContainer from '../../core/project/ProjectContainer';
import ProjectData from '../../core/project/ProjectData';
import ProjectExport from '../../core/project/ProjectExport';
import ProjectImportContainer from '../../core/project/ProjectImportContainer';
import ProjectPermissions from '../../core/project/ProjectPermissions';
import ProjectSummary from '../../core/project/ProjectSummary';
import { UserConsumer } from '../../core/providers/UserProvider';
import ReportManager from '../../core/report/ReportManager';
import Unsaved from '../../core/Unsaved';
import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import VersionManager from '../../core/version/VersionManager';
import TaskManager from '../../core/workflow/TaskManager';
import GS from '../../grafsync/grafsync';
import AdvancedSearch from '../advanced-search/AdvancedSearch';
import OntologyProjectAssociations from '../../application-core/associated-project/OntologyProjectAssociations';
import CollectionsManager from '../collections/CollectionsManager';
import FloatingManager from '../floating/FloatingManager';
import TaxonomyManager from '../taxonomies/TaxonomyManager';
import ProjectReload from './ProjectReload';
import VocabularyProjectDetail from './VocabularyProjectDetail';
import VocabularyProjectInfoContainer from './VocbabularyProjectInfoContainer';

const options = [
  {
    name: "Vocabulary Manager Home",
    url: '',
    icon: <VocabularyManagerIcon />
  },
  ...BASE_OPTIONS,
  ...[
    {
      name: "Template Manager",
      url: 'project/templatemanager',
      icon: <BoardIcon />,
    },
    {
      name: "Import",
      url: 'project/import',
      entitlement: APPENTITLEMENTS.CAN_IMPORT,
      icon: <PublishIcon />
    },
    {
      name: "Export Project",
      url: 'project/export',
      entitlement: APPENTITLEMENTS.CAN_EXPORT_PROJECT,
      icon: <GetAppIcon />
    },
    {
      name: "Free-floating Items",
      url: 'project/floating',
      icon: <WavesIcon />
    },
    {
      name: "Advanced Search",
      url: 'project/search',
      icon: <PageviewIcon />
    },
    {
      name: "Editor",
      url: 'project/editor',
      icon: <EditIcon />
    },
    {
      name: "Reports",
      url: 'project/report',
      icon: <AssessmentIcon />
    },
    {
      name: "Jobs Manager",
      url: 'project/jobsmanager',
      icon: <AssignmentIndIcon />
    },
    {
      name: "Versions",
      url: 'project/version',
      icon: <MemoryIcon />
    }
  ]];

const viewOptions = [
  {
    value: "http://www.w3.org/2004/02/skos/core#prefLabel",
    label: "Preferred Label",
    visible: true,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#altLabel",
    label: "Alternative Labels",
    visible: true,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    label: "Class",
    visible: true,
    format: "class",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "iri",
    label: "IRI",
    visible: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#hiddenLabel",
    alias: "hiddenLabel",
    label: "Hidden Labels",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#broader",
    alias: "broader",
    label: "Broader",
    visible: false,
    format: "relation",
    enableProjectSelection: false,
    canFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#related",
    alias: "related",
    label: "Related",
    visible: false,
    format: "relation",
    enableProjectSelection: false,
    canFilter: true
  },
  {
    value: "notation",
    alias: "notation",
    label: "Notations",
    visible: false,
    format: "datatype"
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#definition",
    alias: "definition",
    joinAlias: "comment",
    label: "Definitions",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#example",
    alias: "example",
    joinAlias: "comment",
    label: "Examples",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#editorialNote",
    alias: "editorialNote",
    joinAlias: "comment",
    label: "Editorial Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#scopeNote",
    alias: "scopeNote",
    joinAlias: "comment",
    label: "Scope Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#historyNote",
    alias: "historyNote",
    joinAlias: "comment",
    label: "History Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  },
  {
    value: "http://www.w3.org/2004/02/skos/core#changeNote",
    alias: "changeNote",
    joinAlias: "comment",
    label: "Change Notes",
    visible: false,
    format: "langString",
    canFilter: true,
    quickFilter: true
  }
];

class VocabularyProject extends Component {

  state = {
    showSidebar: true
  };

  render() {

    const { id, applicationURL, applicationTitle, graph, lockedGraphs, loadKey } = this.props;
    const baseLink = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/detail';
    const refreshKey = id + loadKey;

    const pageCrumb = <Switch>
      <Route path={applicationURL + "/project/detail"} render={() => <span>Project Home</span>} />
      <Route path={applicationURL + "/project/import"} render={() => <span>Import Taxonomy</span>} />
      <Route path={applicationURL + "/project/export"} render={() => <span>Export Project</span>} />
      <Route path={applicationURL + "/project/edit"} render={() => <span>Project Information</span>} />
      <Route path={applicationURL + "/project/permissions"} render={() => <span>Permissions</span>} />
      <Route path={applicationURL + "/project/templatemanager"} render={() => <span>Template Manager</span>} />
      <Route path={applicationURL + "/project/floating"} render={() => <span>Free-floating Items</span>} />
      <Route path={applicationURL + "/project/search"} render={() => <span>Advanced Search</span>} />
      <Route path={applicationURL + "/project/editor"} render={() => <span>Editor</span>} />
      <Route path={applicationURL + "/project/report"} render={() => <span>Reports</span>} />
      <Route path={applicationURL + "/project/version"} render={() => <span>Versions</span>} />
      <Route path={applicationURL + "/project/jobsmanager"} render={() => <span>Jobs Manager</span>} />
      <Route render={() => <span>Edit</span>} />
    </Switch>;

    // PrivateRoute is used if application level entitlements
    return (
      <UserConsumer>
        {({ user, entitlements }) => {
          // Must have this application level entitlement to do any of this
          if (!entitlements.includes(ENTITLEMENTS.VIEW_TAXONOMY_PROJECT)) {
            return <Redirect to={{ pathname: '/' }} />;
          }

          return <PageLayout
            title={applicationTitle}
            baseURL={applicationURL}
            graph={graph}
            id={id}
            showSidebar={this.state.showSidebar}
            options={options}
            includeJumps={true}
            optionParams={"#project=" + id}
            layoutTitle={ProjectSummary}
            layoutContainer={ProjectContainer}
            breadcrumb={{
              baseURL: applicationURL,
              title: applicationTitle,
              static: pageCrumb,
              path: ["project", "taxonomy", "collection", "floating", "search", "editor", "report", "version", "jobsmanager"],
              url: {
                project: "detail",
                taxonomy: "taxonomy",
                collection: "collection",
                floating: "floating",
                search: "search",
                editor: "editor",
                report: "report",
                version: "version",
                jobsmanager: "jobsmanager"
              }
            }}>
            <Unsaved />
            <Switch>
              <Route path={applicationURL + "/project/detail"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props}
                  passthroughComponent={VocabularyProjectDetail}
                  key={refreshKey}
                  id={id}
                  projectType="TaxonomyProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <PrivateRoute path={applicationURL + "/project/import"}
                entitlement={
                  [
                    APPENTITLEMENTS.CAN_IMPORT
                  ]
                }
                component={VocabularyProjectInfoContainer}
                loadKey={loadKey}
                passthroughComponent={GS.outstandingEdits() > 0 ? Unsaved : ProjectImportContainer}
                user={user}
                allowCounter={true}
                configOptions="taxonomy"
                projectType="TaxonomyProject"
                project={id}
                baseLink={baseLink}
                lockedGraphs={lockedGraphs}
                graph={graph}
                id={id} />
              <PrivateRoute path={applicationURL + "/project/export"}
                entitlement={
                  [
                    APPENTITLEMENTS.CAN_EXPORT_PROJECT
                  ]
                }
                component={ProjectExport}
                exportName="taxonomyprojectexport"
                projectType="TaxonomyProject"
                graph={graph}
                id={id} />
              <Route path={applicationURL + "/project/edit"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={ProjectData}
                  key={refreshKey}
                  projectType="TaxonomyProject"
                  entitlement={ENTITLEMENTS.EDIT_TAXONOMY_PROJECT}
                  user={user}
                  graph={graph}
                  project={id} />} />
              <PrivateRoute path={applicationURL + "/project/permissions"}
                entitlement={
                  [
                    APPENTITLEMENTS.EDIT_PERMISSIONS
                  ]
                }
                component={VocabularyProjectInfoContainer}
                passthroughComponent={ProjectPermissions}
                loadKey={loadKey}
                id={id}
                lockedGraphs={lockedGraphs}
                projectType="TaxonomyProject"
                user={user}
                graph={graph}
                project={id} />
              <Route path={applicationURL + "/project/templatemanager"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={OntologyProjectAssociations}
                  key={refreshKey}
                  baseLink={baseLink}
                  projectType="TaxonomyProject"
                  id={id}
                  graph={graph}
                  user={user}
                  project={id} />} />
              <Route path={applicationURL + "/project/taxonomy"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={TaxonomyManager}
                  key={refreshKey}
                  id={id}
                  projectType="TaxonomyProject"
                  graph={graph}
                  user={user}
                  project={id} />} />
              <Route path={applicationURL + "/project/reload"} render={(props) =>
                <ProjectReload {...props} {...this.props} />} />
              <Route path={applicationURL + "/project/collection"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={CollectionsManager}
                  key={refreshKey}
                  id={id}
                  projectType="TaxonomyProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/floating"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={FloatingManager}
                  key={refreshKey}
                  id={id}
                  projectType="TaxonomyProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/search"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={AdvancedSearch}
                  key={refreshKey}
                  id={id}
                  editMode={false}
                  baseLink={APPLICATION_PATHS.TAXONOMY_MANAGER}
                  componentType="Taxonomy"
                  rowDisplay={ConceptResultDisplay}
                  label="prefLabel"
                  resourceIn="http://www.w3.org/2004/02/skos/core#inScheme"
                  labelIRI="http://www.w3.org/2004/02/skos/core#prefLabel"
                  searchClass="http://www.w3.org/2004/02/skos/core#Concept"
                  viewOptions={viewOptions}
                  projectType="TaxonomyProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/editor"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={AdvancedSearch}
                  key={refreshKey}
                  id={id}
                  editMode={true}
                  editRequirements={ENTITLEMENTS.EDIT_TAXONOMY}
                  baseLink={APPLICATION_PATHS.TAXONOMY_MANAGER}
                  componentType="Taxonomy"
                  viewOptions={viewOptions}
                  rowDisplay={ConceptResultDisplay}
                  label="prefLabel"
                  resourceIn="http://www.w3.org/2004/02/skos/core#inScheme"
                  labelIRI="http://www.w3.org/2004/02/skos/core#prefLabel"
                  searchClass="http://www.w3.org/2004/02/skos/core#Concept"
                  projectType="TaxonomyProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/report"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={ReportManager}
                  key={refreshKey}
                  id={id}
                  user={user}
                  projectType="TaxonomyProject"
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/version"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={VersionManager}
                  key={refreshKey}
                  id={id}
                  projectType="TaxonomyProject"
                  exportName="taxonomyprojectexport"
                  user={user}
                  graph={graph}
                  historyOption={{
                    label: "All Resources In Project",
                    resource: true,
                    path: "http://www.w3.org/2004/02/skos/core#inScheme"
                  }}
                  project={id} />} />
              <Route path={applicationURL + "/project/jobsmanager"} render={(props) =>
                <VocabularyProjectInfoContainer {...props} {...this.props} passthroughComponent={TaskManager}
                  key={refreshKey}
                  id={id}
                  showType="http://www.w3.org/2004/02/skos/core#Concept"
                  componentType="Taxonomy"
                  enableSuggestions={true}
                  projectType="TaxonomyProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
            </Switch>
          </PageLayout>;
        }}
      </UserConsumer>
    );
  }
}

VocabularyProject.propTypes = {
  applicationTitle: PropTypes.string.isRequired,
  applicationURL: PropTypes.string.isRequired,
  lockedGraphs: PropTypes.array,
  loadKey: PropTypes.string,
  id: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired
};

export default VocabularyProject;